import React, { useEffect, useState } from 'react';
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Rating, Typography, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { putUserIdPasswordAPI, putUserIdPenaltyAPI } from 'apis/settings/settingsAPI';
import { FaBomb } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { Modal } from 'antd';
import { putUserIdAbortAPI, putUserIdActiveAPI, putUserNameInitPasswordAPI } from 'apis/auth/authAPI';
interface EmployeePopupProps {
  isOpen: boolean;
  handleClose: () => void;
  visitorData: {
    id: number;
    username: string;
    loginStatus?: string;
    name: string;
    type: string;
    email: string;
    phoneNumber: string;
    companyName: string;
    companyPhoneNumber?: string;
    penaltyCount?: number;
    groupName?: string; //임시
  } | null; // visitorData가 null일 수 있으므로 null 허용
}

export default function VisitorPopup({ isOpen, handleClose, visitorData }: EmployeePopupProps) {
  const [penaltyCount, setPenaltyCount] = useState<number>(0);
  // 다국어
  const [language, setLanguage] = useState(localStorage.getItem('language') || 'ko');
  const { t, i18n } = useTranslation(); // useTranslation 훅 사용
  // 다크 라이트 모드
  const theme = useTheme();

  // 비밀번호 초기화 버튼 클릭 핸들
  const handleUserIdPasswordResetBtn = async (userName: string) => {
    const data = { newPassword: '0000' };
    try {
      // API
      const res = await putUserNameInitPasswordAPI(userName);
      // console.log('비밀번호 초기화 API res data: ', res);
    } catch (error: any) {
      return;
    }
    handleClose(); // 모달을 닫기 위해 부모로부터 받은 handleClose 호출
  };

  const handleCloseAndReset = () => {
    handleClose(); // 모달을 닫기 위해 부모로부터 받은 handleClose 호출
  };

  const handlePenaltyCountChange = async (event: React.SyntheticEvent, newValue: number) => {
    const prevPenaltyCount = penaltyCount;
    let newPenaltyCount = newValue;
    if (penaltyCount == newValue) {
      newPenaltyCount = 0;
    }
    setPenaltyCount(newPenaltyCount);

    const data = { penaltyCount: newPenaltyCount };

    try {
      const response = await putUserIdPenaltyAPI(data, visitorData?.id || 0);
      if (response.status === 200 || response.status === 201) {
        setPenaltyCount(newPenaltyCount);
      } else {
        setPenaltyCount(prevPenaltyCount);
        // console.log('error');
      }
    } catch (error: any) {
      setPenaltyCount(prevPenaltyCount);
      return;
    }
  };

  const getStatusText = (status: string | undefined) => {
    switch (status) {
      case 'INITIALIZED':
      case 'ACTIVE':
        return '정상';
      case 'ABORTED':
        return '정지';
      default:
        return '알 수 없음';
    }
  };

  const statusText = getStatusText(visitorData?.loginStatus);

  const handleUserIdActiveAbortBtn = async (userId: number, action: 'activate' | 'deactivate') => {
    const data = {}; // 필요한 데이터 구성 (빈 객체일 경우 필요에 따라 추가)

    try {
      let response;

      if (action === 'activate') {
        // '정지' 상태에서 '활성화'로 변경
        response = await putUserIdActiveAPI(data, userId);
        Modal.success({
          title: '활성화 완료',
          content: '사용자가 활성화되었습니다.',
          onOk: () => {
            handleCloseAndReset();
          }
        });
      } else if (action === 'deactivate') {
        // '정상' 상태에서 '정지'로 변경
        response = await putUserIdAbortAPI(data, userId);
        Modal.success({
          title: '정지 완료',
          content: '사용자가 정지되었습니다.',
          onOk: () => {
            handleCloseAndReset();
          }
        });
      }

      // 성공적인 API 호출 후 처리 (예: UI 업데이트, 상태 변경 등)
      // console.log('API 호출 성공:', response);
    } catch (error) {
      // console.error('회원 상태 변경 중 오류 발생:', error);
      console.error(error);
      Modal.error({
        title: '오류 발생',
        content: '회원 상태 변경 중 오류가 발생했습니다. 다시 시도해주세요.'
      });
    }
  };

  useEffect(() => {
    setPenaltyCount(visitorData?.penaltyCount || 0);
  }, [visitorData]);

  useEffect(() => {
    const handleStorageChange = () => {
      setLanguage(localStorage.getItem('language') || 'ko');
    };
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  return (
    <Dialog
      open={isOpen}
      onClose={handleCloseAndReset}
      sx={{
        width: { xs: '90%', sm: '700px' }, // 모바일에서는 90% 너비
        margin: 'auto'
      }}
    >
      <DialogTitle className="headerTitle" sx={{ bgcolor: theme.palette.table.header, border: 'none' }}>
        <span>{t('account_management.external_user')}</span>
        {/* 엑스 버튼 오른쪽에 배치 */}
        <IconButton onClick={handleCloseAndReset} sx={{ marginLeft: 'auto' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          width: { xs: '100%', sm: '400px' }, // 모바일에서는 가로 제한 없음
          bgcolor: theme.palette.contentBlock.background,
          paddingY: '20px !important'
        }}
      >
        <Box display="flex" flexDirection="column" gap={1}>
          <Box display="flex" flexDirection="row" alignItems="center" justifyContent="space-between" gap={1}>
            <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
              <Typography width={80} variant="caption" color={theme.palette.fieldText.title}>
                {t('account_management.popup.id')}
              </Typography>
              <Typography variant="h6" fontWeight="bold" color="primary.dark">
                {visitorData?.username}
              </Typography>
            </Box>

            <Button
              variant="contained"
              color="error"
              sx={{ borderRadius: '4px', width: '120px', fontSize: '12px', fontWeight: 'bold' }}
              onClick={() => {
                if (visitorData?.username !== undefined) {
                  handleUserIdPasswordResetBtn(visitorData.username);
                }
              }}
            >
              {t('account_management.password_reset')}
            </Button>
          </Box>

          <Box display="flex" flexDirection="row" alignItems="center" justifyContent="space-between" gap={1}>
            <Box display="flex" flexDirection="row" alignItems="center" gap={1} height={24}>
              <Typography width={80} variant="caption" color={theme.palette.fieldText.title}>
                {t('account_management.popup.status')}
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {statusText}
              </Typography>
            </Box>

            <Button
              variant="contained"
              color={statusText === '정지' ? 'success' : 'error'} // 상태에 따라 버튼 색상 변경
              sx={{ borderRadius: '4px', width: '120px', fontSize: '12px', fontWeight: 'bold' }}
              onClick={() => {
                if (visitorData?.id !== undefined) {
                  // 상태가 '정지'일 때는 활성화, '정상'일 때는 정지 처리
                  const action = statusText === '정지' ? 'activate' : 'deactivate';
                  handleUserIdActiveAbortBtn(visitorData.id, action);
                }
              }}
            >
              {statusText === '정지' ? t('account_management.popup.activation') : t('account_management.popup.deactivation')}
            </Button>
          </Box>

          <Box display="flex" flexDirection="row" alignItems="center" gap={1} height={24}>
            <Typography width={80} variant="caption" color={theme.palette.fieldText.title}>
              {t('account_management.popup.name')}
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              {visitorData?.name}
            </Typography>
            <Chip size="small" label={visitorData?.type || '-'} sx={{ fontSize: '10px', height: 18, userSelect: 'none', fontWeight: 'bold' }} />
          </Box>

          <Box display="flex" flexDirection="row" alignItems="center" gap={1} height={24}>
            <Typography width={80} variant="caption" color={theme.palette.fieldText.title}>
              {t('account_management.popup.phone')}
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              {visitorData?.phoneNumber}
            </Typography>
          </Box>

          <Box display="flex" flexDirection="row" alignItems="center" gap={1} height={24}>
            <Typography width={80} variant="caption" color={theme.palette.fieldText.title}>
              {t('account_management.popup.email')}
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              {visitorData?.email}
            </Typography>
          </Box>

          <Box display="flex" flexDirection="row" alignItems="center" gap={1} height={24}>
            <Typography width={80} variant="caption" color={theme.palette.fieldText.title}>
              {t('account_management.popup.company_info')}
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              {visitorData?.companyName} ({visitorData?.companyPhoneNumber || t('account_management.popup.no_company_number')})
            </Typography>
          </Box>

          <Box display="flex" flexDirection="row" alignItems="center" gap={1} height={24}>
            <Typography width={80} variant="caption" color={theme.palette.fieldText.title}>
              {t('account_management.popup.penalty')}
            </Typography>
            <Rating
              name="penalty-rating"
              value={penaltyCount}
              onChange={(event, newValue) => handlePenaltyCountChange(event, newValue || 0)}
              getLabelText={(value: number) => `${value} Penalties`}
              precision={1}
              icon={<FaBomb fontSize="inherit" />}
              emptyIcon={<FaBomb fontSize="inherit" style={{ opacity: 0.5 }} />}
              // sx={{ '& .MuiRating-iconFilled': {
              //     color: '#213719'
              //   } }}
            />
          </Box>
        </Box>
      </DialogContent>
      {/* 확인 버튼 */}
      {/*<DialogActions sx={{ padding: 2, paddingTop: 0 }}>*/}
      {/*  <Button variant="contained" color="primary" fullWidth sx={{ borderRadius: '4px' }} onClick={handleSaveBtn}>*/}
      {/*    저장*/}
      {/*  </Button>*/}
      {/*</DialogActions>*/}
    </Dialog>
  );
}
